import { AppearanceProvider } from './theme/ThemeContext';
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ParentProvider } from "./context/ParentContext";
import { MobileProvider, useMobileContext } from "./context/MobileContext";
import { PlayerDisplayProvider } from "./context/PlayerDisplayContext";
import { ModulesProvider } from "./context/ModulesContext";
import { WebSocketProvider } from "./context/WebSocketContext";
import { ChatViewProvider } from "./context/ChatViewContext";
import { PresentationViewProvider } from "./context/PresentationViewContext";
import { TestSessionProvider } from './features/tests/context/TestSessionContext';
import { PwaUpdateProvider } from "./context/PwaUpdateContext";
import PwaUpdateBanner from "./components/PwaUpdateBanner";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import { Layout } from "./components/Layout";
import { RouteModuleGuard } from "./components/ModuleGuard";
import { isMarketingSite, appUrl } from "./lib/domain";

// Core pages (eagerly loaded)
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import SocialContinue from "./pages/SocialContinue";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import MagicLink from "./pages/MagicLink";
import VerifyEmail from "./pages/VerifyEmail";
import AcceptInvitation from "./pages/AcceptInvitation";
import Team from "./pages/Team";
import EvaluationFormV2Preview from "./components/evaluations/EvaluationFormV2Preview";
import MobileReflectionFlowPreview from "./components/mobile/MobileReflectionFlowPreview";
import Settings from "./pages/Settings";

// Heavy pages (lazy loaded)
const CalendarPage = lazy(() => import("./pages/Calendar"));
const Training = lazy(() => import("./pages/Training"));
const Matches = lazy(() => import("./pages/MatchesWorkspace"));
const Chat = lazy(() => import("./pages/Chat"));
const Statistics = lazy(() => import("./pages/Statistics"));
const Assessments = lazy(() => import("./pages/Assessments"));
const Evaluations = lazy(() => import("./pages/Evaluations"));
const Media = lazy(() => import("./pages/Media"));
const PraiseScreen = lazy(() => import("./pages/PraiseScreen"));
const GapAnalysis = lazy(() => import("./pages/GapAnalysis"));
const AnimationEditorPage = lazy(() => import("./pages/AnimationEditorPage"));
const SharedExerciseVideo = lazy(() => import("./pages/SharedExerciseVideo"));
const ExerciseVisualEmbed = lazy(() => import("./pages/ExerciseVisualEmbed"));
const MatchCenterEmbed = lazy(() => import("./pages/MatchCenterEmbed"));
const ChatEmbed = lazy(() => import("./pages/ChatEmbed"));
const AnimationStudioPage = lazy(() => import("./pages/AnimationStudioPage"));
const Feedback = lazy(() => import("./pages/Feedback"));
const Posts = lazy(() => import("./pages/Posts"));
const Challenges = lazy(() => import("./pages/Challenges"));
const Tests = lazy(() => import("./pages/Tests"));
const TeamInvitations = lazy(() => import("./pages/TeamInvitations"));
// Tactic library now lives in the match workspace; editor routes remain stable.
const TacticStudio = lazy(() => import("./pages/TacticStudio"));
const BoardStudio = lazy(() => import('./pages/BoardStudio'));
const PlatformExercises = lazy(() => import("./pages/PlatformExercises"));
const ExerciseStudio = lazy(() => import("./pages/ExerciseStudio"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const Development = lazy(() => import("./pages/Development"));
const IndividualTraining = lazy(() => import("./pages/IndividualTraining"));
const IndividualTrainingPlanDetail = lazy(() =>
  import("./pages/IndividualTrainingPlanDetail")
);
const MyIndividualTraining = lazy(() => import("./pages/MyIndividualTraining"));
const PlayerProfile = lazy(() => import("./pages/PlayerProfile"));
const SharedMatchReportPage = lazy(() => import("./components/match/MatchReports").then(module => ({ default: module.SharedMatchReportPage })));

// Development conversations
const DevelopmentConversationSession = lazy(() =>
  import("./pages/DevelopmentConversationSession")
);

// Team development overview
const TeamDevelopmentOverview = lazy(() =>
  import("./pages/TeamDevelopmentOverview")
);

const TeamSettings = lazy(() => import("./pages/TeamSettings"));
const TeamOnboarding = lazy(() => import("./pages/TeamOnboarding"));
const NotificationSettingsPage = lazy(() =>
  import("./components/notifications/NotificationSettings")
);
const MatchDetailPage = lazy(() => import("./pages/MatchDetailPage"));

// Home Pages (lazy)
const AcademyOverview = lazy(() => import("./pages/AcademyOverview"));

// Platform Admin Pages (lazy)
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminOrganizationDetail = lazy(() =>
  import("./pages/AdminOrganizationDetail")
);
const PlatformRequests = lazy(() => import("./pages/PlatformRequests"));
const PlatformRequestDetail = lazy(() =>
  import("./pages/PlatformRequestDetail")
);
const PlatformCustomers = lazy(() => import("./pages/PlatformCustomers"));
const PlatformCustomerDetail = lazy(() =>
  import("./pages/PlatformCustomerDetail")
);
const PlatformSupport = lazy(() => import("./pages/PlatformSupport"));
const PlatformSupportDetail = lazy(() =>
  import("./pages/PlatformSupportDetail")
);
const SupportTeamEntry = lazy(() => import("./pages/SupportTeamEntry"));

// Organization Admin Pages - SPRINT V3.3 (lazy)
const ClubExerciseStudio = lazy(() => import('./pages/ClubExerciseStudio'));
const OrganizationDashboard = lazy(() =>
  import("./pages/OrganizationDashboard")
);
const OrganizationTeams = lazy(() => import("./pages/OrganizationTeams"));
const OrganizationRoles = lazy(() => import("./pages/OrganizationRoles"));
const OrganizationMembers = lazy(() =>
  import("./pages/OrganizationMembers")
);

// New Identity Components
import SelfServiceOnboarding from "./pages/SelfServiceOnboarding";
import CreateAdditionalTeam from "./pages/CreateAdditionalTeam";
import BillingReturn from "./pages/BillingReturn";
import { TeamPausedScreen } from "./components/TeamPausedScreen";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import JoinPage from "./pages/JoinPage";

// Lazy loading fallback
const LazyFallback = () => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="animate-pulse text-primary text-sm">Laddar...</div>
  </div>
);

// Protected Route Component
function ProtectedRoute({
  children,
  checkModule = true,
  requireTeam = false,
}) {
  const {
    isAuthenticated,
    loading,
    needsOnboarding,
    activeTeam,
    user,
  } = useAuth();

  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Laddar...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve original path so the user lands back after login.
    const back = `${location.pathname}${location.search || ""}`;
    const suffix =
      back && back !== "/" ? `?next=${encodeURIComponent(back)}` : "";

    return <Navigate to={`/login${suffix}`} replace />;
  }

  // Redirect to onboarding if user needs it
  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Central commercial gate: a self-service team whose owner's Trainnova Coach
  // subscription lost access is commercially paused.
  // commercial_access is resolved by backend.
  if (activeTeam && activeTeam.commercial_access === false) {
    const isOwner = activeTeam.created_by_user_id === user?.id;

    return (
      <Layout>
        <TeamPausedScreen team={activeTeam} isOwner={isOwner} />
      </Layout>
    );
  }

  // Team-required routes
  if (requireTeam && !activeTeam) {
    return (
      <Layout>
        <TeamRequiredState />
      </Layout>
    );
  }

  // Wrap with module guard if needed
  if (checkModule) {
    return (
      <Layout>
        <RouteModuleGuard>
          <Suspense fallback={<LazyFallback />}>{children}</Suspense>
        </RouteModuleGuard>
      </Layout>
    );
  }

  return (
    <Layout>
      <Suspense fallback={<LazyFallback />}>{children}</Suspense>
    </Layout>
  );
}

// Team-required route
function TeamRoute(props) {
  return <ProtectedRoute {...props} requireTeam />;
}

// ONE shared zero-team state
function TeamRequiredState() {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-[60vh] flex items-center justify-center px-6"
      data-testid="team-required-state"
    >
      <div className="max-w-md text-center space-y-4">
        <h2 className="text-2xl font-semibold tracking-tight">
          Skapa ett lag för att använda den här funktionen
        </h2>

        <p className="text-muted-foreground">
          Ditt konto är aktivt. Skapa ditt lag när du är redo för att komma åt
          kalender, träningar, matcher och mer.
        </p>

        <button
          onClick={() => navigate("/create-team")}
          data-testid="team-required-create-btn"
          className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-6 h-11 text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          Skapa lag
        </button>
      </div>
    </div>
  );
}

// Authenticated create-team entry
function CreateTeamRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Laddar...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Onboarding Route
function OnboardingRoute({ children }) {
  const { isAuthenticated, loading, needsOnboarding } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Laddar...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!needsOnboarding) {
    return <SmartHomeRedirect />;
  }

  return children;
}

// Smart Home Redirect
function SmartHomeRedirect() {
  const {
    isAuthenticated,
    loading,
    teams,
  } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Laddar...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/welcome" replace />;
  }

  const teamsLoaded = teams !== null && teams !== undefined;

  if (!teamsLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Laddar...</div>
      </div>
    );
  }

  // All roles → unified overview
  const targetPath = "/oversikt";

  return <Navigate to={targetPath} replace />;
}

// Desktop-Only Route Guard
function DesktopOnlyRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const { isMobile } = useMobileContext();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Laddar...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isMobile) {
    return <Navigate to="/oversikt" replace />;
  }

  return <Layout>{children}</Layout>;
}

// Public Route
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Laddar...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    let nextTarget = null;

    try {
      const raw = new URLSearchParams(location.search).get("next");

      if (raw && raw.startsWith("/") && !raw.startsWith("//")) {
        nextTarget = raw;
      }
    } catch {
      // noop
    }

    if (nextTarget) {
      return <Navigate to={nextTarget} replace />;
    }

    return <SmartHomeRedirect />;
  }

  return children;
}

// App Admin Route
function AppAdminRoute({ children }) {
  const {
    isAuthenticated,
    loading,
    user,
  } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Laddar...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.global_role !== "app_admin") {
    return <Navigate to="/oversikt" replace />;
  }

  return <Layout>{children}</Layout>;
}

// Redirect to app subdomain
function ExternalRedirect({ to }) {
  React.useEffect(() => {
    window.location.replace(appUrl(to));
  }, [to]);

  return null;
}

// Preserve the team code when a public marketing URL like /join/ABC123
// is opened. The marketing domain must forward the complete deep link
// to the application domain instead of collapsing it to /join.
function ExternalJoinRedirect() {
  const { code } = useParams();
  const target = code ? `/join/${encodeURIComponent(code)}` : "/join";

  return <ExternalRedirect to={target} />;
}

function AppRoutes() {
  // Marketing domain
  if (isMarketingSite) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/welcome" element={<LandingPage />} />

        <Route
          path="/login"
          element={<ExternalRedirect to="/login" />}
        />

        <Route
          path="/register"
          element={<ExternalRedirect to="/register" />}
        />

        <Route
          path="/join"
          element={<ExternalRedirect to="/join" />}
        />

        <Route
          path="/join/:code"
          element={<ExternalJoinRedirect />}
        />

        <Route
          path="/auth/magic-link"
          element={<ExternalRedirect to="/auth/magic-link" />}
        />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/continue" element={<SocialContinue />} />

        <Route
          path="/accept-invite"
          element={<ExternalRedirect to="/accept-invite" />}
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Full application
  return (
    <Routes>
      {/* ================= PUBLIC ================= */}

      <Route path="/welcome" element={<LandingPage />} />

      <Route path="/share/exercise/:token" element={<SharedExerciseVideo />} />
      <Route
        path="/embed/exercise-visual/:exerciseId"
        element={<ExerciseVisualEmbed />}
      />

      <Route
        path="/embed/match-center/:matchId"
        element={<MatchCenterEmbed />}
      />

      <Route path="/embed/chat" element={<ChatEmbed />} />

      <Route path="/join" element={<JoinPage />} />
      <Route path="/join/:code" element={<JoinPage />} />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      <Route
        path="/register"
        element={<Register />}
      />

      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/magic-link" element={<MagicLink />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/continue" element={<SocialContinue />} />
      <Route path="/accept-invite" element={<AcceptInvitation />} />

      {/* ================= ONBOARDING ================= */}

      <Route
        path="/onboarding"
        element={
          <OnboardingRoute>
            <OnboardingPage />
          </OnboardingRoute>
        }
      />

      <Route
        path="/create-team"
        element={
          <CreateTeamRoute>
            <CreateAdditionalTeam />
          </CreateTeamRoute>
        }
      />

      {/* ================= BILLING ================= */}

      <Route path="/billing/success" element={<BillingReturn />} />
      <Route path="/billing/cancel" element={<BillingReturn />} />

      {/* ================= HOME ================= */}

      <Route
        path="/oversikt"
        element={
          <ProtectedRoute>
            <AcademyOverview />
          </ProtectedRoute>
        }
      />

      {/* Legacy redirects */}
      <Route path="/home" element={<Navigate to="/oversikt" replace />} />
      <Route
        path="/home/player"
        element={<Navigate to="/oversikt" replace />}
      />
      <Route
        path="/home/coach"
        element={<Navigate to="/oversikt" replace />}
      />
      <Route
        path="/dashboard"
        element={<Navigate to="/oversikt" replace />}
      />
      <Route
        path="/lagstatus"
        element={<Navigate to="/oversikt" replace />}
      />

      {/* ================= CALENDAR ================= */}

      <Route
        path="/calendar"
        element={
          <TeamRoute>
            <CalendarPage />
          </TeamRoute>
        }
      />

      <Route
        path="/calendar/event/:eventId"
        element={
          <TeamRoute>
            <CalendarPage />
          </TeamRoute>
        }
      />

      {/* ================= CHAT ================= */}

      <Route
        path="/chat/*"
        element={
          <TeamRoute>
            <Chat />
          </TeamRoute>
        }
      />

      {/* ================= MATCH ================= */}

      <Route
        path="/matches"
        element={
          <TeamRoute>
            <Matches />
          </TeamRoute>
        }
      />

      <Route
        path="/matches/:matchId"
        element={
          <TeamRoute>
            <MatchDetailPage />
          </TeamRoute>
        }
      />

      <Route
        path="/matches/:matchId/live"
        element={
          <TeamRoute>
            <MatchDetailPage />
          </TeamRoute>
        }
      />

      <Route
        path="/matches/:matchId/lineup"
        element={
          <TeamRoute>
            <MatchDetailPage />
          </TeamRoute>
        }
      />

      <Route
        path="/matches/:matchId/tactics"
        element={
          <TeamRoute>
            <MatchDetailPage />
          </TeamRoute>
        }
      />

      <Route
        path="/matches/:matchId/log"
        element={
          <TeamRoute>
            <MatchDetailPage />
          </TeamRoute>
        }
      />

      <Route
        path="/matches/:matchId/analysis"
        element={
          <TeamRoute>
            <MatchDetailPage />
          </TeamRoute>
        }
      />

      <Route
        path="/matches/:matchId/assessment"
        element={
          <TeamRoute>
            <MatchDetailPage />
          </TeamRoute>
        }
      />

      {/* Legacy match route */}
      <Route
        path="/match/:matchId"
        element={
          <TeamRoute>
            <MatchDetailPage />
          </TeamRoute>
        }
      />

      {/* ================= TRAINING ================= */}

      <Route
        path="/training"
        element={
          <TeamRoute>
            <Training />
          </TeamRoute>
        }
      />

      <Route
        path="/training/library"
        element={
          <ProtectedRoute>
            <Training />
          </ProtectedRoute>
        }
      />

      <Route
        path="/training/library/:exerciseId"
        element={
          <ProtectedRoute>
            <Training />
          </ProtectedRoute>
        }
      />

      <Route
        path="/training/session/:sessionId"
        element={
          <TeamRoute>
            <Training />
          </TeamRoute>
        }
      />

      <Route
        path="/training/session/:sessionId/block/:blockId"
        element={
          <TeamRoute>
            <Training />
          </TeamRoute>
        }
      />

      <Route
        path="/training/session/:sessionId/exercise/:exerciseId"
        element={
          <TeamRoute>
            <Training />
          </TeamRoute>
        }
      />

      <Route
        path="/training/exercises/new"
        element={
          <ProtectedRoute>
            <ExerciseStudio />
          </ProtectedRoute>
        }
      />

      <Route
        path="/training/exercises/:exerciseId/edit"
        element={
          <ProtectedRoute>
            <ExerciseStudio />
          </ProtectedRoute>
        }
      />

      <Route
        path="/training/:eventId"
        element={
          <TeamRoute>
            <Training />
          </TeamRoute>
        }
      />

      {/* ================= DEVELOPMENT ================= */}

      <Route
        path="/development"
        element={
          <TeamRoute>
            <Development />
          </TeamRoute>
        }
      />

      <Route
        path="/utveckling/individuell-traning"
        element={
          <TeamRoute>
            <IndividualTraining />
          </TeamRoute>
        }
      />

      <Route
        path="/utveckling/individuell-traning/:planId"
        element={
          <TeamRoute>
            <IndividualTrainingPlanDetail />
          </TeamRoute>
        }
      />

      <Route
        path="/min-traning"
        element={
          <ProtectedRoute>
            <MyIndividualTraining />
          </ProtectedRoute>
        }
      />

      <Route
        path="/development/evaluations"
        element={
          <TeamRoute>
            <Evaluations />
          </TeamRoute>
        }
      />

      <Route
        path="/development/evaluations/:evaluationId"
        element={
          <TeamRoute>
            <Evaluations />
          </TeamRoute>
        }
      />

      <Route
        path="/development/assessments"
        element={
          <TeamRoute>
            <Assessments />
          </TeamRoute>
        }
      />

      <Route
        path="/development/statistics"
        element={
          <TeamRoute>
            <Statistics />
          </TeamRoute>
        }
      />

      <Route
        path="/development/praise"
        element={
          <TeamRoute>
            <PraiseScreen />
          </TeamRoute>
        }
      />

      <Route
        path="/development/challenges"
        element={
          <TeamRoute>
            <Challenges />
          </TeamRoute>
        }
      />

      <Route
        path="/development/tests"
        element={
          <TeamRoute>
            <Tests />
          </TeamRoute>
        }
      />

      {/* Development conversation session */}
      <Route
        path="/development/conversations/:sessionId"
        element={
          <TeamRoute>
            <DevelopmentConversationSession />
          </TeamRoute>
        }
      />

      {/* Legacy development routes */}
      <Route
        path="/evaluations"
        element={<Navigate to="/development/evaluations" replace />}
      />

      <Route
        path="/assessments"
        element={<Navigate to="/development/assessments" replace />}
      />

      <Route
        path="/statistics"
        element={<Navigate to="/development/statistics" replace />}
      />

      <Route
        path="/praise"
        element={<Navigate to="/development/praise" replace />}
      />

      <Route
        path="/challenges"
        element={<Navigate to="/development/challenges" replace />}
      />

      {/* ================= TEAM ================= */}

      <Route
        path="/team"
        element={
          <TeamRoute>
            <Team />
          </TeamRoute>
        }
      />

      {/* NEW — team player development overview */}
      <Route
        path="/team/development"
        element={
          <TeamRoute>
            <TeamDevelopmentOverview />
          </TeamRoute>
        }
      />

      {/* ================= PREVIEWS ================= */}

      <Route
        path="/eval-v2-preview/:templateId"
        element={
          <ProtectedRoute>
            <EvaluationFormV2Preview />
          </ProtectedRoute>
        }
      />

      <Route
        path="/mobile-reflection-preview/:id"
        element={
          <ProtectedRoute>
            <MobileReflectionFlowPreview />
          </ProtectedRoute>
        }
      />

      <Route path="/match-reports/:reportId" element={<ProtectedRoute><SharedMatchReportPage /></ProtectedRoute>} />
      {/* ================= PLAYER ================= */}

      <Route
        path="/players/:playerId"
        element={
          <TeamRoute>
            <PlayerProfile />
          </TeamRoute>
        }
      />

      {/* ================= MEDIA / SETTINGS ================= */}

      <Route
        path="/media"
        element={
          <TeamRoute>
            <Media />
          </TeamRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/notifications"
        element={
          <ProtectedRoute>
            <NotificationSettingsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/team/settings"
        element={
          <TeamRoute>
            <TeamSettings />
          </TeamRoute>
        }
      />

      <Route
        path="/team/members"
        element={
          <TeamRoute>
            <TeamSettings />
          </TeamRoute>
        }
      />

      <Route
        path="/team/onboarding"
        element={
          <TeamRoute>
            <TeamOnboarding />
          </TeamRoute>
        }
      />

      <Route
        path="/team/:teamId/onboarding"
        element={
          <TeamRoute>
            <TeamOnboarding />
          </TeamRoute>
        }
      />

      <Route
        path="/roles"
        element={<Navigate to="/team/settings" replace />}
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPanelPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/gap-analysis"
        element={
          <TeamRoute>
            <GapAnalysis />
          </TeamRoute>
        }
      />

      <Route
        path="/exercises/:exerciseId/animation"
        element={
          <ProtectedRoute>
            <AnimationEditorPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/animation-studio"
        element={<Navigate to="/tactics" replace />}
      />

      <Route
        path="/feedback"
        element={
          <ProtectedRoute>
            <Feedback />
          </ProtectedRoute>
        }
      />

      <Route
        path="/posts"
        element={
          <TeamRoute>
            <Posts />
          </TeamRoute>
        }
      />

      <Route
        path="/team/invitations"
        element={
          <TeamRoute>
            <TeamInvitations />
          </TeamRoute>
        }
      />

      <Route path="/tactics/board" element={<ProtectedRoute><BoardStudio /></ProtectedRoute>} />
      {/* ================= TACTICS ================= */}

      <Route
        path="/tactics"
        element={
          <ProtectedRoute>
            <Navigate to="/matches?view=tactics" replace />
          </ProtectedRoute>
        }
      />

      <Route
        path="/tactics/new"
        element={
          <ProtectedRoute>
            <TacticStudio />
          </ProtectedRoute>
        }
      />

      <Route
        path="/tactics/:id/edit"
        element={
          <ProtectedRoute>
            <TacticStudio />
          </ProtectedRoute>
        }
      />

      {/* ================= PLATFORM ADMIN ================= */}
      <Route path="/platform-admin/exercises/:exerciseId/edit" element={<AppAdminRoute><ExerciseStudio platformMode /></AppAdminRoute>} />
      <Route path="/platform-admin/exercises" element={<AppAdminRoute><PlatformExercises /></AppAdminRoute>} />

      <Route
        path="/platform-admin"
        element={
          <AppAdminRoute>
            <AdminDashboard />
          </AppAdminRoute>
        }
      />

      <Route
        path="/platform-admin/customers"
        element={
          <AppAdminRoute>
            <PlatformCustomers />
          </AppAdminRoute>
        }
      />

      <Route
        path="/platform-admin/customers/:ownerUserId"
        element={
          <AppAdminRoute>
            <PlatformCustomerDetail />
          </AppAdminRoute>
        }
      />

      <Route
        path="/platform-admin/support"
        element={
          <AppAdminRoute>
            <PlatformSupport />
          </AppAdminRoute>
        }
      />

      <Route
        path="/platform-admin/support/:ticketId"
        element={
          <AppAdminRoute>
            <PlatformSupportDetail />
          </AppAdminRoute>
        }
      />

      <Route
        path="/platform-admin/requests"
        element={
          <AppAdminRoute>
            <PlatformRequests />
          </AppAdminRoute>
        }
      />

      <Route
        path="/platform-admin/requests/:requestId"
        element={
          <AppAdminRoute>
            <PlatformRequestDetail />
          </AppAdminRoute>
        }
      />

      <Route
        path="/platform-admin/orgs"
        element={
          <AppAdminRoute>
            <AdminDashboard view="organizations" />
          </AppAdminRoute>
        }
      />

      <Route
        path="/platform-admin/orgs/:orgId"
        element={
          <AppAdminRoute>
            <AdminOrganizationDetail />
          </AppAdminRoute>
        }
      />

      <Route
        path="/platform-admin/orgs/:orgId/teams/:teamId/support"
        element={
          <AppAdminRoute>
            <SupportTeamEntry />
          </AppAdminRoute>
        }
      />

      {/* ================= ORGANIZATION ADMIN ================= */}
      <Route path="/org/:orgId/exercises/:exerciseId/edit" element={<ProtectedRoute checkModule={false}><ClubExerciseStudio /></ProtectedRoute>} />

      <Route
        path="/org/:orgId"
        element={
          <ProtectedRoute>
            <OrganizationDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/org/:orgId/dashboard"
        element={
          <ProtectedRoute>
            <OrganizationDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/org/:orgId/teams"
        element={
          <ProtectedRoute>
            <OrganizationTeams />
          </ProtectedRoute>
        }
      />

      <Route
        path="/org/:orgId/roles"
        element={
          <ProtectedRoute>
            <OrganizationRoles />
          </ProtectedRoute>
        }
      />

      <Route
        path="/org/:orgId/members"
        element={
          <ProtectedRoute>
            <OrganizationMembers />
          </ProtectedRoute>
        }
      />

      {/* ================= ROOT ================= */}

      <Route path="/" element={<SmartHomeRedirect />} />

      {/* Catch all */}
      <Route path="*" element={<SmartHomeRedirect />} />
    </Routes>
  );
}

// Wrapper pages for components
function OnboardingPage() {
  return <SelfServiceOnboarding />;
}

// Legacy /admin redirect
function AdminPanelPage() {
  const { user } = useAuth();

  const isPlatformAdmin =
    user?.global_role === "app_admin" ||
    user?.global_role === "platform_admin" ||
    user?.role === "admin";

  return (
    <Navigate
      to={isPlatformAdmin ? "/platform-admin" : "/team"}
      replace
    />
  );
}

function App() {
  return (
    <BrowserRouter>
      <PwaUpdateProvider>
        <AuthProvider>
          <AppearanceProvider>
          <ParentProvider>
            <WebSocketProvider>
              <MobileProvider>
                <ChatViewProvider>
                  <PresentationViewProvider>
                    <PlayerDisplayProvider>
                      <ModulesProvider>
                        <TestSessionProvider>
                        <ErrorBoundary>
                          <AppRoutes />
                        </ErrorBoundary>

                        <Toaster richColors position="top-center" />

                        <PWAInstallPrompt />

                        <PwaUpdateBanner />
                        </TestSessionProvider>
                      </ModulesProvider>
                    </PlayerDisplayProvider>
                  </PresentationViewProvider>
                </ChatViewProvider>
              </MobileProvider>
            </WebSocketProvider>
          </ParentProvider>
        </AppearanceProvider>
        </AuthProvider>
      </PwaUpdateProvider>
    </BrowserRouter>
  );
}

export default App;
